import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ContainerTemplateSchema,
  ContainerTemplateDiscoverySchema,
  type NewContainer,
  type ContainerTemplate,
} from '@sr/types';
import { WithCustomerPortalApi } from 'core/customerPortalApi';
import { useFlag } from 'core/lib/hooks/useFlag';

type Params = {
  baseUrl: string;
  parentLocation: string;
  workflow: 'add-container' | 'new-location';
};
const createHook = (
  fetchFn: (args: {
    url: string;
    options: RequestInit;
    type?: 'json' | 'text';
  }) => Promise<unknown>,
  { baseUrl, parentLocation, workflow }: Params,
) => {
  const enabled = useFlag('container-templates');
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['container-templates', baseUrl, parentLocation, workflow],
    enabled: enabled && !!parentLocation,
    queryFn: async () =>
      ContainerTemplateDiscoverySchema.parse(
        await fetchFn({
          url: `${baseUrl}?${new URLSearchParams({ partnerDwKey: parentLocation, workflow })}`,
          options: { method: 'GET' },
        }),
      ),
  });
  const invalidate = () => client.invalidateQueries({ queryKey: ['container-templates', baseUrl] });
  const write = useMutation({
    mutationFn: async (input: {
      id?: string;
      partnerDwKey: string;
      workflow?: Params['workflow'];
      data: { name: string; containers: NewContainer[] };
    }) =>
      ContainerTemplateSchema.parse(
        await fetchFn({
          url: baseUrl,
          options: {
            method: input.id ? 'PUT' : 'POST',
            body: JSON.stringify({ ...input, schemaVersion: 1 }),
          },
        }),
      ),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (record: ContainerTemplate) =>
      fetchFn({
        url: baseUrl,
        type: 'text',
        options: {
          method: 'DELETE',
          body: JSON.stringify({ id: record.id, partnerDwKey: record.partnerDwKey }),
        },
      }),
    onSuccess: invalidate,
  });

  return {
    enabled,
    templates: query.data?.records ?? [],
    error: query.error,
    isPending: query.isPending || write.isPending || remove.isPending,
    create: {
      mutateAsync: async (data: { name: string; containers: NewContainer[] }) => {
        if (!parentLocation || query.error) throw new Error('Template context is unavailable');
        await write.mutateAsync({ partnerDwKey: parentLocation, workflow, data });
      },
    },
    update: async (record: ContainerTemplate, containers: NewContainer[]) => {
      await write.mutateAsync({
        id: record.id,
        partnerDwKey: record.partnerDwKey,
        data: { name: record.data.name, containers },
      });
    },
    remove,
  };
};

export const useContainerTemplates = WithCustomerPortalApi<
  unknown,
  ReturnType<typeof createHook>,
  Params
>((fetchFn, params) => createHook(fetchFn, params), { hookName: 'useContainerTemplates' });
